import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import SmsIcon from "@mui/icons-material/SmsOutlined";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDeviceStatus,
  sendStatusSms,
  sendSoftwareUpdate,
} from "./companyDevicesSlice";
import {
  fieldsForDeviceType,
  formatStatusValue,
} from "./statusMapper";

const REFRESH_SECONDS = 6;

export default function StatusPanel({ device, onClose }) {
  const dispatch = useDispatch();
  const status = useSelector(
    (s) => s.companyDevices.statusByImei[device.IMEI]
  );
  const [loading, setLoading] = useState(true);
  const [counter, setCounter] = useState(REFRESH_SECONDS);
  const intervalRef = useRef(null);
  // Device-command dialog. Opens from the company-name chip in the
  // header. Hosts two MQTT-backed actions:
  //   - Status SMS:    needs a phone number, publishes
  //                    `{st:5,data:"<phone>"}` to the IMEI topic
  //   - Software Update: no phone needed, publishes the firmware
  //                    update payload to the IMEI topic
  // `inFlight` is the name of the action currently dispatched (so we
  // can disable both buttons + show a spinner on the right one).
  // `lastResult` shows a small "✓ Status SMS sent" / error banner
  // after each action so the operator sees feedback without closing.
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  const [inFlight, setInFlight] = useState(null); // "status" | "swupdate" | null
  const [lastResult, setLastResult] = useState(null); // { ok, label, message }
  // First Software-Update click flips this to true, swapping the "Send
  // Update" button for an inline Confirm / Cancel pair. Avoids nesting a
  // separate Dialog inside the popup.
  const [confirmingSwUpdate, setConfirmingSwUpdate] = useState(false);

  // The Device record's company can be either a populated `{ _id, name }`
  // object (from the global /api/devices list endpoint) or a bare
  // ObjectId string (from the per-company endpoint). Only render the
  // clickable chip when we actually have a name to show.
  const companyName =
    device?.company && typeof device.company === "object"
      ? device.company.name
      : null;

  const onSendStatus = async () => {
    const trimmed = smsPhone.trim();
    if (!trimmed) {
      setLastResult({
        ok: false,
        label: "Status SMS",
        message: "Enter a phone number first",
      });
      return;
    }
    setInFlight("status");
    setLastResult(null);
    const res = await dispatch(
      sendStatusSms({ imei: device.IMEI, phone: trimmed })
    );
    setInFlight(null);
    if (sendStatusSms.fulfilled.match(res)) {
      setLastResult({
        ok: true,
        label: "Status SMS",
        message: `Sent to ${trimmed}`,
      });
    } else {
      setLastResult({
        ok: false,
        label: "Status SMS",
        message: res.payload || "Send failed",
      });
    }
  };

  const onSendSoftwareUpdate = async () => {
    setInFlight("swupdate");
    setLastResult(null);
    const res = await dispatch(sendSoftwareUpdate(device.IMEI));
    setInFlight(null);
    setConfirmingSwUpdate(false);
    if (sendSoftwareUpdate.fulfilled.match(res)) {
      setLastResult({
        ok: true,
        label: "Software Update",
        message: "Command published to device",
      });
    } else {
      setLastResult({
        ok: false,
        label: "Software Update",
        message: res.payload || "Send failed",
      });
    }
  };

  const closeSms = () => {
    if (inFlight) return;
    setSmsOpen(false);
    setSmsPhone("");
    setLastResult(null);
    setConfirmingSwUpdate(false);
  };

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const res = await dispatch(fetchDeviceStatus(device.IMEI));
      if (!cancelled && fetchDeviceStatus.fulfilled.match(res)) setLoading(false);
    };
    tick();

    intervalRef.current = setInterval(() => {
      setCounter((c) => {
        if (c <= 1) {
          tick();
          return REFRESH_SECONDS;
        }
        return c - 1;
      });
    }, 1000);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [device.IMEI, dispatch]);

  const fields = fieldsForDeviceType(device.device_type);

  const grouped = [];
  let bucket = null;
  fields.forEach((f) => {
    if (f.type === "header") {
      bucket = { title: f.label, items: [] };
      grouped.push(bucket);
    } else if (bucket) {
      bucket.items.push(f);
    }
  });

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Sticky meta strip — device name + type chip + IMEI + actions
          stay pinned to the top as the long status grid scrolls below.
          backgroundColor is required so body content doesn't bleed
          through; the box-shadow + bottom border give a clean cut. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          backgroundColor: "background.paper",
          mb: 1,
          py: 0.75,
          // Pull out a touch on either side so the sticky band visually
          // covers full-width even though the outer Box has p:1.5.
          mx: -1.5,
          px: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {device.device_name || device.IMEI}
          </Typography>
          <Chip
            label={Number(device.device_type) === 2 ? "Tank" : Number(device.device_type) === 3 ? "Valve" : "Motor"}
            size="small"
            sx={{ height: 18, fontSize: "0.6875rem", borderRadius: "2px" }}
          />
          {/* Only show the IMEI line when the device actually has a name —
              otherwise the IMEI is already the headline above and the
              second monospace copy is just visual noise. */}
          {device.device_name && (
            <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
              {device.IMEI}
            </Typography>
          )}
          {/* Clickable company-name chip → opens the Send Status SMS
              dialog. Falls back to a plain button when the device's
              company is not populated (per-company tab context). */}
          {companyName ? (
            <Tooltip title="Send status SMS">
              <Chip
                label={companyName}
                size="small"
                icon={<SmsIcon sx={{ fontSize: 14 }} />}
                clickable
                onClick={() => setSmsOpen(true)}
                sx={{
                  height: 20,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  backgroundColor: "primary.light",
                  color: "primary.dark",
                  "& .MuiChip-icon": { color: "primary.dark" },
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Send status SMS">
              <Button
                size="small"
                variant="outlined"
                startIcon={<SmsIcon sx={{ fontSize: 14 }} />}
                onClick={() => setSmsOpen(true)}
                sx={{
                  py: 0,
                  px: 0.75,
                  minHeight: 20,
                  fontSize: "0.6875rem",
                  lineHeight: 1.2,
                }}
              >
                Send Status
              </Button>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={loading ? "loading…" : `refreshes in ${counter}s`}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.625rem",
              borderRadius: "2px",
              backgroundColor: loading ? "#fdf2e6" : "#e8f5ec",
              color: loading ? "#df6e0c" : "#107e3e",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && !status ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : !status ? (
        <Typography variant="caption" color="text.secondary">
          No status data available for this IMEI.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {grouped.map((g) => (
            <Box key={g.title}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "primary.main",
                  fontSize: "0.625rem",
                  mb: 0.5,
                }}
              >
                {g.title}
              </Typography>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "6px",
                  overflow: "hidden",
                  backgroundColor: "background.paper",
                }}
              >
                {g.items.map((f, idx) => (
                  <Stack
                    key={f.key + f.label}
                    direction="row"
                    alignItems="center"
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderBottom:
                        idx === g.items.length - 1 ? 0 : "1px solid",
                      borderColor: "divider",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Typography
                      sx={{
                        flexShrink: 0,
                        width: { xs: 130, sm: 170 },
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {f.label}
                      {/* Reference key (d1, d2, i1, etc.) in parens so
                          admins can map the friendly label back to the
                          raw firmware field. */}
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontFamily: "monospace",
                          fontSize: "0.625rem",
                          fontWeight: 600,
                          color: "text.disabled",
                        }}
                      >
                        ({f.key})
                      </Box>
                    </Typography>
                    <Typography
                      sx={{
                        flexShrink: 0,
                        color: "text.disabled",
                        fontSize: "0.75rem",
                        mr: 0.75,
                      }}
                    >
                      :
                    </Typography>
                    <Typography
                      sx={{
                        flex: 1,
                        fontFamily: "monospace",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "text.primary",
                        wordBreak: "break-word",
                      }}
                    >
                      {formatStatusValue(f, status?.[f.key])}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {/* Device commands popup. Inline phone input + two action buttons
          (Status SMS / Software Update) in the screenshot style. Last
          action's result lands in a small banner below so the operator
          sees confirmation without closing the dialog. */}
      <Dialog
        open={smsOpen}
        onClose={closeSms}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <Box
          sx={{
            p: 1.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <SmsIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Device Commands
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.25 }}
          >
            IMEI <b style={{ fontFamily: "monospace" }}>{device.IMEI}</b>
            {companyName && (
              <>
                {" · "}
                {companyName}
              </>
            )}
          </Typography>
        </Box>
        <DialogContent sx={{ p: 2 }}>
          {/* Phone + Status button — the inline pattern from the
              screenshot. Enter on the phone field also fires Status. */}
          <Stack direction="row" spacing={1} alignItems="stretch">
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Phone Number"
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.target.value)}
              autoFocus
              disabled={Boolean(inFlight)}
              inputProps={{ inputMode: "tel", autoComplete: "tel" }}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSendStatus();
              }}
              sx={{
                "& .MuiInputBase-input": { fontSize: "0.8125rem" },
              }}
            />
            <Button
              onClick={onSendStatus}
              variant="contained"
              color="primary"
              disabled={Boolean(inFlight) || !smsPhone.trim()}
              startIcon={
                inFlight === "status" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ flexShrink: 0, minWidth: 88 }}
            >
              {inFlight === "status" ? "…" : "Status"}
            </Button>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5, fontSize: "0.6875rem" }}
          >
            Status SMS: device texts its current readings to this number.
            Type the full number including country code (e.g.{" "}
            <code>917708665003</code>) — the leading <code>+</code> is
            added automatically.
          </Typography>

          {/* Software Update — separate row, no phone needed. Stays in
              this dialog AND on the row action menu (operators are
              still learning where it lives). One click flips to an
              inline Confirm prompt so it can't fire by accident. */}
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px dashed",
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.8125rem", fontWeight: 600 }}
                >
                  Software Update
                </Typography>
                <Typography
                  variant="caption"
                  color={confirmingSwUpdate ? "warning.dark" : "text.secondary"}
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: confirmingSwUpdate ? 600 : 400,
                  }}
                >
                  {confirmingSwUpdate
                    ? "Send firmware-update command to this device?"
                    : "Push firmware-update command over MQTT."}
                </Typography>
              </Box>
              {confirmingSwUpdate ? (
                <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                  <Button
                    onClick={() => setConfirmingSwUpdate(false)}
                    variant="outlined"
                    size="small"
                    disabled={Boolean(inFlight)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={onSendSoftwareUpdate}
                    variant="contained"
                    color="warning"
                    size="small"
                    disabled={Boolean(inFlight)}
                    startIcon={
                      inFlight === "swupdate" ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <SystemUpdateAltIcon fontSize="small" />
                      )
                    }
                    sx={{ minWidth: 110 }}
                  >
                    {inFlight === "swupdate" ? "Sending…" : "Yes, send"}
                  </Button>
                </Stack>
              ) : (
                <Button
                  onClick={() => setConfirmingSwUpdate(true)}
                  variant="outlined"
                  disabled={Boolean(inFlight)}
                  startIcon={<SystemUpdateAltIcon fontSize="small" />}
                  sx={{ flexShrink: 0, minWidth: 130 }}
                >
                  Send Update
                </Button>
              )}
            </Stack>
          </Box>

          {/* Last action result. Greens on success, reds on failure,
              shows which action and a short message. Cleared when a new
              action starts. */}
          {lastResult && (
            <Box
              sx={{
                mt: 1.5,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                backgroundColor: lastResult.ok
                  ? "rgba(16, 126, 62, 0.08)"
                  : "rgba(185, 28, 28, 0.08)",
                color: lastResult.ok ? "success.dark" : "error.dark",
              }}
            >
              {lastResult.ok ? (
                <CheckCircleIcon sx={{ fontSize: 16, mt: 0.125 }} />
              ) : (
                <ErrorOutlineIcon sx={{ fontSize: 16, mt: 0.125 }} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ display: "block", fontWeight: 700, fontSize: "0.75rem" }}
                >
                  {lastResult.label}: {lastResult.ok ? "Sent" : "Failed"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ display: "block", fontSize: "0.6875rem" }}
                >
                  {lastResult.message}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={closeSms} variant="outlined" disabled={Boolean(inFlight)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
